import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Drawer, { PanelSection, PanelInput } from "./Drawer";
import { equipmentApi } from "@/lib/api";
import { confirm } from "@/lib/dialog";

/**
 * Quick equipment CRUD from the schedule sidebar. Per-room allocations are
 * NOT managed here — that lives in the dedicated /equipment view. This panel
 * is only about the master inventory list (add / rename / quantity / category).
 */
export default function EquipmentPanel({ open, onClose, projectId, equipment, onMutated }) {
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState({ type: "", label: "", quantity_total: 1, category: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const startAdd = async () => {
    if (!adding.type.trim()) return;
    setBusy(true);
    try {
      await equipmentApi.create(projectId, {
        type: adding.type.trim().toLowerCase().replace(/\s+/g, "_"),
        label: adding.label.trim() || adding.type,
        quantity_total: Math.max(0, +adding.quantity_total || 0),
        category: adding.category.trim() || null,
        notes: adding.notes || null,
      });
      setAdding({ type: "", label: "", quantity_total: 1, category: "", notes: "" });
      onMutated && (await onMutated());
      toast.success("Equipment added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (eq, updates) => {
    try {
      await equipmentApi.update(eq.id, updates);
      onMutated && (await onMutated());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (eq) => {
    const ok = await confirm({
      title: `Remove "${eq.label || eq.type}"?`,
      body: "Existing allocations to this equipment will also be removed.",
      confirmLabel: "REMOVE",
      danger: true,
    });
    if (!ok) return;
    try {
      await equipmentApi.remove(eq.id);
      onMutated && (await onMutated());
      toast.success("Equipment removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title="EQUIPMENT" count={equipment.length} dataTestid="equipment-panel" width={420}>
      <PanelSection title="INVENTORY">
        <div className="space-y-2">
          {equipment.map((eq) => (
            <EquipmentRow
              key={eq.id}
              equipment={eq}
              editing={editing === eq.id}
              onStartEdit={() => setEditing(eq.id)}
              onCancelEdit={() => setEditing(null)}
              onSave={(updates) => { saveEdit(eq, updates); setEditing(null); }}
              onRemove={() => remove(eq)}
            />
          ))}
        </div>
      </PanelSection>

      <PanelSection title="+ NEW EQUIPMENT">
        <PanelInput label="TYPE (kebab/snake)" value={adding.type} onChange={(v) => setAdding({ ...adding, type: v })} placeholder="dmx_splitter" dataTestid="equipment-type-input" />
        <PanelInput label="LABEL (display)" value={adding.label} onChange={(v) => setAdding({ ...adding, label: v })} placeholder="DMX Splitter — 4-way" />
        <div className="grid grid-cols-2 gap-2">
          <PanelInput label="POOL QTY" type="number" value={adding.quantity_total} onChange={(v) => setAdding({ ...adding, quantity_total: v })} />
          <PanelInput label="CATEGORY" value={adding.category} onChange={(v) => setAdding({ ...adding, category: v })} placeholder="Audio" />
        </div>
        <PanelInput label="NOTES" value={adding.notes} onChange={(v) => setAdding({ ...adding, notes: v })} />
        <button onClick={startAdd} disabled={busy || !adding.type.trim()} className="btn-primary w-full justify-center" data-testid="equipment-add-btn">
          <Plus className="w-3 h-3" /> ADD EQUIPMENT
        </button>
        <div className="mono text-2xs uppercase tracking-widest text-ink-mute mt-2">
          Tip — allocate to rooms in the /equipment view.
        </div>
      </PanelSection>
    </Drawer>
  );
}

function EquipmentRow({ equipment, editing, onStartEdit, onCancelEdit, onSave, onRemove }) {
  const [draft, setDraft] = useState({
    label: equipment.label || "",
    quantity_total: equipment.quantity_total,
    category: equipment.category || "",
    notes: equipment.notes || "",
  });
  useEffect(() => {
    if (editing) {
      setDraft({
        label: equipment.label || "",
        quantity_total: equipment.quantity_total,
        category: equipment.category || "",
        notes: equipment.notes || "",
      });
    }
  }, [editing, equipment]);

  if (!editing) {
    return (
      <div className="flex items-center gap-2 p-2 border border-line bg-bg-elevated hover:border-line-strong group">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-ink truncate">{equipment.label || equipment.type}</div>
          <div className="mono text-2xs uppercase tracking-wider text-ink-mute mt-0.5">
            POOL {equipment.quantity_total}
            {equipment.category && <span className="ml-2 text-amber">{equipment.category}</span>}
          </div>
        </div>
        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
          <button onClick={onStartEdit} className="btn-ghost">EDIT</button>
          <button onClick={onRemove} className="text-ink-mute hover:text-red"><Trash2 className="w-3 h-3" /></button>
        </div>
      </div>
    );
  }
  return (
    <div className="p-2 border border-amber bg-bg-elevated space-y-2">
      <PanelInput label="LABEL" value={draft.label} onChange={(v) => setDraft({ ...draft, label: v })} />
      <div className="grid grid-cols-2 gap-2">
        <PanelInput label="POOL QTY" type="number" value={draft.quantity_total} onChange={(v) => setDraft({ ...draft, quantity_total: v })} />
        <PanelInput label="CATEGORY" value={draft.category} onChange={(v) => setDraft({ ...draft, category: v })} />
      </div>
      <PanelInput label="NOTES" value={draft.notes} onChange={(v) => setDraft({ ...draft, notes: v })} />
      <div className="flex gap-1">
        <button onClick={() => onSave({ label: draft.label, quantity_total: +draft.quantity_total || 0, category: draft.category || "", notes: draft.notes })} className="btn-primary flex-1 justify-center">SAVE</button>
        <button onClick={onCancelEdit} className="btn-ghost">CANCEL</button>
      </div>
    </div>
  );
}
